import { doc, getDoc, type DocumentData } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_STREET_SURVEY_KEY,
  isDefaultStreetSurvey,
  listSurveyAxes,
  resolveStreetSurveyAiConfig,
  resolveAggregateAxisKeys,
  resolveStreetSurveyExtraction,
  resolveEffectiveAiAnalysis,
  resolveEvaluationAxis,
  resolveLegendAxisKey,
  resolveStreetSurveyDisplay,
  resolveStreetSurveyDisplayLayers,
  STREET_SURVEY_TEMPLATES_COLLECTION,
  type AiAnalysisSettings,
  type AiCategoryMaster,
  type AiOutputFormat,
  type AiOutputFormatSchema,
  type District,
  type EvaluationAxisDef,
  type ResolvedStreetSurveyExtraction,
  type StreetSurveyDisplayDef,
  type StreetSurveyTemplate,
  type SurveyAxisDef,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';
import { buildByCategoryCdMap } from './streetSurveyPinDisplay';
import type { PrimaryAxisResolution } from './streetSurveyAxisTypes';
import type { RoleTypeAxisResolution } from './streetSurveyAxisTypes';

/**
 * ストリートビュー机上調査の「調査項目」設定を実効値で解決する hook (docs/27 §9)。
 *
 * 従来の `usePrimaryAxisCategoryMaster` (primary 軸固定) / `useRoleTypeAxis` (リテラル role_type) は
 * テンプレ (M1 → M3) だけを見て地区の上書き (`outputFormatIdOverride` / `outputFieldsOverride`) を
 * 無視していた。本 hook は functions 側 `effectiveSettings` と同じ順で実効スキーマを解決し、
 *   - 調査項目 (分類軸) の一覧 (`listSurveyAxes`)
 *   - 各軸が参照する M2 マスタ (色・名前)
 *   - 凡例軸 / 集計軸 / 評価列 の既定 (出力フォーマットの `schema.streetSurvey` (移行期間) → 地区に適用した
 *     机上調査テンプレート (docs/27 §17) → 地区 `streetSurveyOverride` → 自動)
 * を返す。既存コンポーネントは `PrimaryAxisResolution` / `RoleTypeAxisResolution` 型を受け取るため、
 * 任意の軸をその形に変換する `asPrimary` / `asRole` を提供する (凡例 = role 形、集計 = primary 形)。
 */

export interface ResolvedSurveyAxis extends SurveyAxisDef {
  master: AiCategoryMaster | null;
  byCategoryCd: Map<string, AiCategoryMaster['rows'][number]>;
}

export interface StreetSurveyConfig {
  loading: boolean;
  settings: AiAnalysisSettings | null;
  /** 地区上書き反映後の実効 M3 スキーマ (無ければ null)。 */
  schema: AiOutputFormatSchema | null;
  /** `useAiOutputFormatBySettings` 互換 (reasoningField 等の参照用)。 */
  outputFormat: AiOutputFormat | null;
  axes: ResolvedSurveyAxis[];
  /** 地区に適用した机上調査テンプレート (docs/27 §17、無ければ null)。 */
  template: (StreetSurveyTemplate & { id: string }) | null;
  /** 地区の上書きの下の層 (出力フォーマットの既定 + テンプレート)。上書きの編集画面の「引き継ぐ値」に使う。 */
  inheritedDisplay: StreetSurveyDisplayDef;
  /** 3 層を重ねた実効の設定。 */
  display: StreetSurveyDisplayDef;
  evaluation: EvaluationAxisDef | null;
  /** 既定の凡例軸 key / 集計軸 key (無ければ null)。 */
  defaultLegendAxisKey: string | null;
  defaultAggregateAxisKey: string | null;
  asPrimary: (axisKey: string | null | undefined) => PrimaryAxisResolution;
  asRole: (axisKey: string | null | undefined) => RoleTypeAxisResolution;
  /** 解析パイプライン v2 の実効設定 (docs/27 §15-3)。 */
  extraction: ResolvedStreetSurveyExtraction;
  /** 読み込んだマスタ (id → マスタ)。v2 のプロンプトのプレビューに使う。 */
  mastersById: ReadonlyMap<string, AiCategoryMaster>;
}

const _settingsCache = new Map<string, AiAnalysisSettings | null>();

async function loadTemplate(id: string): Promise<(StreetSurveyTemplate & { id: string }) | null> {
  try {
    const snap = await getDoc(doc(db, STREET_SURVEY_TEMPLATES_COLLECTION, id));
    return snap.exists() ? { ...(snap.data() as StreetSurveyTemplate), id: snap.id } : null;
  } catch {
    return null;
  }
}
const _formatCache = new Map<string, AiOutputFormat | null>();
const _masterCache = new Map<string, AiCategoryMaster | null>();

async function loadDoc<T>(
  cache: Map<string, T | null>,
  collectionName: string,
  id: string,
): Promise<T | null> {
  if (cache.has(id)) return cache.get(id) ?? null;
  try {
    const snap = await getDoc(doc(db, collectionName, id));
    const data = snap.exists() ? ({ ...(snap.data() as DocumentData), id } as T) : null;
    cache.set(id, data);
    return data;
  } catch {
    cache.set(id, null);
    return null;
  }
}

interface Loaded {
  settings: AiAnalysisSettings | null;
  template: (StreetSurveyTemplate & { id: string }) | null;
  outputFormat: AiOutputFormat | null;
  schema: AiOutputFormatSchema | null;
  masters: Map<string, AiCategoryMaster>;
  axes: SurveyAxisDef[];
}

const EMPTY_PRIMARY: PrimaryAxisResolution = {
  primaryAxis: null,
  master: null,
  byCategoryCd: new Map(),
  loading: false,
};
const EMPTY_ROLE: RoleTypeAxisResolution = {
  axis: null,
  master: null,
  byCategoryCd: new Map(),
  loading: false,
};

export function useStreetSurveyConfig(
  district: District | null | undefined,
  settingsIdInput: string | null | undefined,
  /** 調査 (docs/98 R-97、未指定 = 既定の調査)。追加の調査はその調査の解析設定・テンプレート・上書きで解決する。 */
  surveyKey: string = DEFAULT_STREET_SURVEY_KEY,
): StreetSurveyConfig {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(false);
  const districtAi = resolveStreetSurveyAiConfig(district?.aiAnalysis ?? null, surveyKey);
  const settingsId = isDefaultStreetSurvey(surveyKey)
    ? settingsIdInput
    : (districtAi?.settingsId ?? null);
  // 地区上書きの同一性キー (オブジェクト参照ではなく内容で再解決する)。
  const overrideKey = JSON.stringify({
    f: districtAi?.outputFormatIdOverride ?? null,
    o: districtAi?.outputFieldsOverride ?? null,
    m: districtAi?.categoryMasterIdsOverride ?? null,
    s: districtAi?.streetSurveyOverride ?? null,
    t: districtAi?.streetSurveyTemplateId ?? null,
  });
  const templateId = districtAi?.streetSurveyTemplateId ?? null;

  useEffect(() => {
    if (!settingsId) {
      setLoaded(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const settings = await loadDoc<AiAnalysisSettings>(
        _settingsCache,
        'aiAnalysisSettings',
        settingsId,
      );
      // テンプレートはマスタ画面で編集されうるため、キャッシュせず毎回読む。
      const template = templateId ? await loadTemplate(templateId) : null;
      if (!settings) {
        if (!cancelled)
          setLoaded({
            settings: null,
            template,
            outputFormat: null,
            schema: null,
            masters: new Map(),
            axes: [],
          });
        return;
      }
      // functions 側 effectiveSettings と同じ順: outputFormatIdOverride → outputFieldsOverride。
      const resolved = resolveEffectiveAiAnalysis(settings, districtAi);
      const formatId = resolved.outputFormatId.value ?? settings.outputFormatId ?? null;
      const outputFormat = formatId
        ? await loadDoc<AiOutputFormat>(_formatCache, 'aiOutputFormats', formatId)
        : null;
      let schema: AiOutputFormatSchema | null = outputFormat?.schema ?? null;
      const fieldsOverride = resolved.outputFieldsOverride.value;
      if (schema && fieldsOverride && fieldsOverride.length > 0) {
        schema = { ...schema, fields: fieldsOverride };
      }
      const axes = listSurveyAxes(schema);
      // 軸が参照するマスタ + settings のマスタ (masterId 未設定軸のフォールバック用先頭)。
      const masterIds = new Set<string>();
      for (const a of axes) if (a.masterId) masterIds.add(a.masterId);
      const settingsMasterIds =
        resolved.categoryMasterIds.value ??
        settings.categoryMasterIds ??
        (settings.categoryMasterId ? [settings.categoryMasterId] : []);
      for (const id of settingsMasterIds) masterIds.add(id);
      const masters = new Map<string, AiCategoryMaster>();
      await Promise.all(
        [...masterIds].map(async (id) => {
          const m = await loadDoc<AiCategoryMaster>(_masterCache, 'aiCategoryMasters', id);
          if (m) masters.set(id, m);
        }),
      );
      if (cancelled) return;
      // masterId 未設定の軸は settings の先頭マスタで補う (functions の `masters[0]` 慣習)。
      const fallbackMasterId = settingsMasterIds[0] ?? null;
      const withFallback = axes.map((a) =>
        a.masterId || !fallbackMasterId ? a : { ...a, masterId: fallbackMasterId },
      );
      setLoaded({ settings, template, outputFormat, schema, masters, axes: withFallback });
    })()
      .catch(() => {
        if (!cancelled)
          setLoaded({
            settings: null,
            template: null,
            outputFormat: null,
            schema: null,
            masters: new Map(),
            axes: [],
          });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // districtAi は overrideKey で同一性を判定する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsId, overrideKey]);

  return useMemo<StreetSurveyConfig>(() => {
    const schema = loaded?.schema ?? null;
    const masters = loaded?.masters ?? new Map<string, AiCategoryMaster>();
    const axes: ResolvedSurveyAxis[] = (loaded?.axes ?? []).map((a) => {
      const master = a.masterId ? (masters.get(a.masterId) ?? null) : null;
      return { ...a, master, byCategoryCd: buildByCategoryCdMap(master?.rows) };
    });
    const template = loaded?.template ?? null;
    const inheritedDisplay = resolveStreetSurveyDisplay(schema?.streetSurvey, template?.display);
    const display = resolveStreetSurveyDisplayLayers(
      schema?.streetSurvey,
      template?.display,
      districtAi?.streetSurveyOverride,
    );
    const evaluation = resolveEvaluationAxis(schema, axes, masters, display);
    const extraction = resolveStreetSurveyExtraction(schema, display, {
      legacyModeResults: districtAi?.legacyModeResults === true,
    });
    const defaultLegendAxisKey = resolveLegendAxisKey(axes, display);
    const defaultAggregateAxisKey =
      resolveAggregateAxisKeys(axes, display, evaluation?.field ?? null)[0] ?? null;
    const find = (key: string | null | undefined) =>
      key ? (axes.find((a) => a.key === key || a.codeField === key) ?? null) : null;
    return {
      loading,
      settings: loaded?.settings ?? null,
      schema,
      outputFormat: loaded?.outputFormat ?? null,
      axes,
      template,
      inheritedDisplay,
      display,
      evaluation,
      extraction,
      mastersById: masters,
      defaultLegendAxisKey,
      defaultAggregateAxisKey,
      asPrimary: (key) => {
        const a = find(key);
        return a
          ? { primaryAxis: a.axis, master: a.master, byCategoryCd: a.byCategoryCd, loading }
          : { ...EMPTY_PRIMARY, loading };
      },
      asRole: (key) => {
        const a = find(key);
        return a
          ? { axis: a.axis, master: a.master, byCategoryCd: a.byCategoryCd, loading }
          : { ...EMPTY_ROLE, loading };
      },
    };
  }, [loaded, loading, districtAi?.streetSurveyOverride, districtAi?.legacyModeResults]);
}
